import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetPersonByIdQuery, useGetPersonTicketsQuery, useGetPersonDealsQuery, useGetPersonTasksQuery, useRefreshZendeskDataMutation } from "../../redux/api/personsApi";
import { formatPrice } from "../../utils/currencyUtils";
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Alert,
  Row,
  Col,
  Spin,
  message,
  Divider,
  Badge
} from "antd";
import {
  EditOutlined,
  ReloadOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  HomeOutlined,
  ShoppingOutlined,
  CustomerServiceOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";

const { Title, Text } = Typography;

const PersonDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: person, isLoading, error, refetch } = useGetPersonByIdQuery(id);
  const { 
    data: ticketsData, 
    isLoading: ticketsLoading, 
    error: ticketsError 
  } = useGetPersonTicketsQuery(id, {
    skip: isLoading || !person?.zendesk_support_id,
  });
  const { 
    data: dealsData, 
    isLoading: dealsLoading, 
    error: dealsError 
  } = useGetPersonDealsQuery(id, {
    skip: isLoading || !person?.zendesk_sell_contact_id,
  });
  const { 
    data: tasksData, 
    isLoading: tasksLoading, 
    error: tasksError 
  } = useGetPersonTasksQuery(id, {
    skip: isLoading || !person?.zendesk_sell_contact_id,
  });
  const [refreshZendesk, { isLoading: isRefreshing }] = useRefreshZendeskDataMutation();

  const handleRefreshZendesk = async () => {
    try {
      const result = await refreshZendesk(id).unwrap();
      refetch();
      message.success(result.message);
    } catch (err) {
      message.error(err?.data?.message || 'Failed to refresh Zendesk data');
    }
  };

  // Helper functions
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  const formatCurrency = (value, currency = 'USD') => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error loading customer"
        description={error?.data?.message || error?.error || 'Failed to load customer details'}
        type="error"
        showIcon
      />
    );
  }

  if (!person) {
    return (
      <Alert
        message="Customer not found"
        description="The customer you're looking for doesn't exist."
        type="warning"
        showIcon
      />
    );
  }

  // Construct Zendesk URLs
  const zendeskSupportUrl = person.zendesk_subdomain && person.zendesk_support_id
    ? `https://${person.zendesk_subdomain}.zendesk.com/agent/users/${person.zendesk_support_id}`
    : null;
  
  const zendeskSellUrl = person.zendesk_sell_contact_id
    ? `https://app.futuresimple.com/contacts/${person.zendesk_sell_contact_id}`
    : null;

  // Tickets table columns
  const ticketsColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => `#${id}`,
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
      render: (subject) => subject || '(No subject)',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusLower = status?.toLowerCase();
        let color = 'default';
        if (statusLower === 'new') color = 'blue';
        else if (statusLower === 'open') color = 'cyan';
        else if (statusLower === 'pending') color = 'orange';
        else if (statusLower === 'solved' || statusLower === 'closed') color = 'green';
        return <Tag color={color}>{status || 'Unknown'}</Tag>;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority) => {
        const priorityLower = priority?.toLowerCase();
        let color = 'default';
        if (priorityLower === 'urgent') color = 'red';
        else if (priorityLower === 'high') color = 'orange';
        else if (priorityLower === 'normal' || priorityLower === 'medium') color = 'blue';
        else if (priorityLower === 'low') color = 'default';
        return <Tag color={color}>{priority || 'Normal'}</Tag>;
      },
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      render: (date) => formatDateTime(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => {
        const ticketUrl = person.zendesk_subdomain
          ? `https://${person.zendesk_subdomain}.zendesk.com/agent/tickets/${record.id}`
          : null;
        return ticketUrl ? (
          <Button
            type="link"
            size="small"
            href={ticketUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View
          </Button>
        ) : null;
      },
    },
  ];

  // Deals table columns
  const dealsColumns = [
    {
      title: 'Deal Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text || 'Unnamed Deal'}</strong>,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: 120,
      render: (value, record) => formatCurrency(value, record.currency),
    },
    {
      title: 'Stage',
      dataIndex: 'stage_name',
      key: 'stage_name',
      width: 150,
      render: (stageName) => {
        const name = stageName?.toLowerCase() || '';
        let color = 'default';
        if (name.includes('won') || name.includes('closed')) color = 'success';
        else if (name.includes('lost') || name.includes('unqualified')) color = 'error';
        else if (name.includes('negotiat') || name.includes('propos')) color = 'warning';
        else if (name.includes('qualif')) color = 'processing';
        return <Tag color={color}>{stageName || 'Unknown'}</Tag>;
      },
    },
    {
      title: 'Hot',
      dataIndex: 'hot',
      key: 'hot',
      width: 60,
      align: 'center',
      render: (hot) => hot ? '🔥' : '-',
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          href={`https://app.futuresimple.com/sales/deals/${record.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          View
        </Button>
      ),
    },
  ];

  // Tasks table columns
  const tasksColumns = [
    {
      title: 'Task',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (content) => content || '(No content)',
    },
    {
      title: 'Deal',
      dataIndex: 'deal_name',
      key: 'deal_name',
      width: 200,
      ellipsis: true,
      render: (dealName, record) => {
        if (!dealName && !record.taskable_name) return '-';
        const dealUrl = `https://app.futuresimple.com/sales/deals/${record.taskable_id}`;
        return (
          <Button
            type="link"
            size="small"
            href={dealUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ padding: 0 }}
          >
            {dealName || record.taskable_name || 'View Deal'}
          </Button>
        );
      },
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 120,
      render: (owner) => owner || '-',
    },
    {
      title: 'Due Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => {
        const isOverdue = record.date && !record.done_at && new Date(record.date) < new Date();
        const isDone = !!record.done_at;
        if (isDone) return <Tag color="success" icon={<CheckCircleOutlined />}>Done</Tag>;
        if (isOverdue) return <Tag color="error" icon={<ExclamationCircleOutlined />}>Overdue</Tag>;
        return <Tag color="warning" icon={<ClockCircleOutlined />}>Pending</Tag>;
      },
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <Title level={1} style={{ margin: 0, marginBottom: 8, fontSize: '28px', fontWeight: 600 }}>
            {person.first_name} {person.last_name}
          </Title>
          <Space size="small" wrap>
            {person.person_type && (
              <Tag color="blue">{person.person_type}</Tag>
            )}
            {person.zendesk_support_id && (
              <Text type="secondary" style={{ fontSize: '0.875rem' }}>
                Support: {person.zendesk_support_id}
              </Text>
            )}
            {person.zendesk_sell_contact_id && (
              <Text type="secondary" style={{ fontSize: '0.875rem' }}>
                Sell: {person.zendesk_sell_contact_id}
              </Text>
            )}
          </Space>
        </div>
        <Space wrap>
          {zendeskSupportUrl ? (
            <Button
              icon={<CustomerServiceOutlined />}
              href={zendeskSupportUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Zendesk Support
            </Button>
          ) : (
            <Button icon={<CustomerServiceOutlined />} disabled>
              Zendesk Support
            </Button>
          )}
          {zendeskSellUrl ? (
            <Button
              icon={<DollarOutlined />}
              href={zendeskSellUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Zendesk Sell
            </Button>
          ) : (
            <Button icon={<DollarOutlined />} disabled>
              Zendesk Sell
            </Button>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefreshZendesk}
            loading={isRefreshing}
            disabled={!person?.email}
            title={!person?.email ? "Email required to lookup Zendesk" : "Lookup Zendesk Sell contact by email"}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/persons/edit/${id}`)}
          >
            Edit
          </Button>
        </Space>
      </div>

      {/* Main Content - Single Page Layout */}
      <Row gutter={[16, 16]}>
        {/* Customer Information - Left Column */}
        <Col xs={24} lg={16}>
          <Card 
            title={
              <Space>
                <UserOutlined />
                <span>Customer Information</span>
              </Space>
            }
          >
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12}>
                <Space orientation="vertical" size={4}>
                  <Text type="secondary" style={{ fontSize: '0.875rem' }}>Name</Text>
                  <Text strong style={{ fontSize: '1rem' }}>
                    {person.first_name} {person.last_name}
                  </Text>
                </Space>
              </Col>
              <Col xs={24} sm={12}>
                <Space orientation="vertical" size={4}>
                  <Text type="secondary" style={{ fontSize: '0.875rem' }}>Email</Text>
                  {person.email ? (
                    <Space>
                      <MailOutlined style={{ color: '#1890ff' }} />
                      <a href={`mailto:${person.email}`} style={{ fontSize: '0.95rem' }}>
                        {person.email}
                      </a>
                    </Space>
                  ) : (
                    <Text type="secondary">Not provided</Text>
                  )}
                </Space>
              </Col>
              <Col xs={24} sm={12}>
                <Space orientation="vertical" size={4}>
                  <Text type="secondary" style={{ fontSize: '0.875rem' }}>Phone</Text>
                  {person.phone ? (
                    <Space>
                      <PhoneOutlined style={{ color: '#1890ff' }} />
                      <a href={`tel:${person.phone}`} style={{ fontSize: '0.95rem' }}>
                        {person.phone}
                      </a>
                    </Space>
                  ) : (
                    <Text type="secondary">Not provided</Text>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Addresses - Right Column */}
        <Col xs={24} lg={8}>
          <Card 
            title={
              <Space>
                <HomeOutlined />
                <span>Addresses</span>
              </Space>
            }
          >
            {person.addresses && person.addresses.length > 0 ? (
              <Space orientation="vertical" style={{ width: '100%' }} size="small">
                {person.addresses.map((address, idx) => (
                  <div
                    key={address.address_id || idx}
                    style={{
                      padding: '12px',
                      backgroundColor: '#fafafa',
                      border: '1px solid #f0f0f0',
                      borderRadius: '4px'
                    }}
                  >
                    <Space orientation="vertical" size={4} style={{ width: '100%' }}>
                      {address.address_type_name && (
                        <Tag color="default" style={{ marginBottom: 4 }}>
                          {address.address_type_name}
                        </Tag>
                      )}
                      <Text style={{ display: 'block', fontSize: '0.875rem', lineHeight: '1.6' }}>
                        {address.street && <>{address.street}<br /></>}
                        {address.floor !== null && address.floor !== undefined && address.floor !== "" && (
                          <>Floor {address.floor}<br /></>
                        )}
                        {address.postal_code && address.city ? (
                          <>{address.postal_code} {address.city}</>
                        ) : address.city ? (
                          <>{address.city}</>
                        ) : address.postal_code ? (
                          <>{address.postal_code}</>
                        ) : null}
                        {address.state_province || address.state ? (
                          <><br />{address.state_province || address.state}</>
                        ) : null}
                        {address.country && (
                          <><br />{address.country}</>
                        )}
                      </Text>
                    </Space>
                  </div>
                ))}
              </Space>
            ) : (
              <Text type="secondary">No addresses available</Text>
            )}
          </Card>
        </Col>

        {/* Order History */}
        <Col xs={24}>
          <Card 
            title={
              <Space>
                <ShoppingOutlined />
                <span>Order History</span>
              </Space>
            }
          >
            {person.orders && person.orders.length > 0 ? (
              <Table
                columns={[
                  { title: 'Order ID', dataIndex: 'id', key: 'id', width: 100 },
                  { title: 'Date', dataIndex: 'date', key: 'date', width: 150 },
                  { 
                    title: 'Total', 
                    dataIndex: 'total', 
                    key: 'total',
                    render: (total) => formatPrice(total, 'CZK')
                  },
                ]}
                dataSource={person.orders}
                rowKey="id"
                pagination={false}
                size="middle"
              />
            ) : (
              <Text type="secondary">No orders found</Text>
            )}
          </Card>
        </Col>

        {/* Zendesk Tickets */}
        {person.zendesk_support_id && (
          <Col xs={24}>
            <Card 
              title={
                <Space>
                  <CustomerServiceOutlined />
                  <span>Zendesk Support Tickets</span>
                  {ticketsData?.tickets?.length > 0 && (
                    <Badge count={ticketsData.tickets.length} showZero style={{ backgroundColor: '#1890ff' }} />
                  )}
                </Space>
              }
            >
              {ticketsError ? (
                <Alert
                  message="Error loading tickets"
                  description={ticketsError?.data?.message || ticketsError?.error || 'Unknown error'}
                  type="warning"
                  showIcon
                />
              ) : ticketsLoading ? (
                <Spin />
              ) : ticketsData?.tickets && ticketsData.tickets.length > 0 ? (
                <Table
                  columns={ticketsColumns}
                  dataSource={ticketsData.tickets}
                  rowKey="id"
                  pagination={false}
                  size="middle"
                />
              ) : (
                <Text type="secondary">No tickets found for this customer</Text>
              )}
            </Card>
          </Col>
        )}

        {/* Zendesk Sell Deals */}
        {person.zendesk_sell_contact_id && (
          <Col xs={24}>
            <Card 
              title={
                <Space>
                  <DollarOutlined />
                  <span>Zendesk Sell Deals</span>
                  {dealsData?.deals?.length > 0 && (
                    <Badge count={dealsData.deals.length} showZero style={{ backgroundColor: '#52c41a' }} />
                  )}
                </Space>
              }
            >
              {dealsError ? (
                <Alert
                  message="Error loading deals"
                  description={dealsError?.data?.message || dealsError?.error || 'Unknown error'}
                  type="warning"
                  showIcon
                />
              ) : dealsLoading ? (
                <Spin />
              ) : dealsData?.deals && dealsData.deals.length > 0 ? (
                <Table
                  columns={dealsColumns}
                  dataSource={dealsData.deals}
                  rowKey="id"
                  pagination={false}
                  size="middle"
                />
              ) : (
                <Text type="secondary">No deals found for this customer</Text>
              )}
            </Card>
          </Col>
        )}

        {/* Zendesk Tasks */}
        {person.zendesk_sell_contact_id && (
          <Col xs={24}>
            <Card 
              title={
                <Space>
                  <CheckCircleOutlined />
                  <span>Zendesk Tasks</span>
                  {tasksData?.meta?.total_count > 0 && (
                    <Badge count={tasksData.meta.total_count} showZero style={{ backgroundColor: '#fa8c16' }} />
                  )}
                </Space>
              }
            >
              {tasksError ? (
                <Alert
                  message="Error loading tasks"
                  description={tasksError?.data?.message || tasksError?.error || 'Unknown error'}
                  type="warning"
                  showIcon
                />
              ) : tasksLoading ? (
                <Spin />
              ) : (tasksData?.deal_tasks?.length > 0 || tasksData?.contact_tasks?.length > 0) ? (
                <Space orientation="vertical" style={{ width: '100%' }} size="middle">
                  {tasksData?.deal_tasks?.length > 0 && (
                    <div>
                      <Divider orientation="left" style={{ marginTop: 0 }}>
                        <Space>
                          <span>Deal Tasks</span>
                          <Tag color="cyan">{tasksData.deal_tasks.length}</Tag>
                        </Space>
                      </Divider>
                      <Table
                        columns={tasksColumns}
                        dataSource={tasksData.deal_tasks}
                        rowKey="id"
                        pagination={false}
                        size="middle"
                        rowClassName={(record) => {
                          const isOverdue = record.date && !record.done_at && new Date(record.date) < new Date();
                          return isOverdue ? 'ant-table-row-warning' : '';
                        }}
                      />
                    </div>
                  )}
                  {tasksData?.contact_tasks?.length > 0 && (
                    <div>
                      <Divider orientation="left">
                        <Space>
                          <span>Contact Tasks</span>
                          <Tag color="blue">{tasksData.contact_tasks.length}</Tag>
                        </Space>
                      </Divider>
                      <Table
                        columns={tasksColumns.filter(col => col.key !== 'deal_name')}
                        dataSource={tasksData.contact_tasks}
                        rowKey="id"
                        pagination={false}
                        size="middle"
                        rowClassName={(record) => {
                          const isOverdue = record.date && !record.done_at && new Date(record.date) < new Date();
                          return isOverdue ? 'ant-table-row-warning' : '';
                        }}
                      />
                    </div>
                  )}
                </Space>
              ) : (
                <Text type="secondary">No tasks found for this customer</Text>
              )}
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default PersonDetailsPage;
